import { useCallback, useId, type ChangeEventHandler } from 'react';
import InfoIcon from 'assets/icons/info.svg';
import MarkdownParser from 'components/rich-text/markdown-parser';
import ViewAllSpreadsIcon from 'assets/icons/view-all-spreads.svg';
import BackToSpreadIcon from 'assets/icons/back-to-spread.svg';
import CloseIcon from 'assets/icons/close-x.svg';
import LoadingIcon from 'assets/icons/loading-spinner.svg';
import CogIcon from 'assets/icons/cog.svg';
import MetLogoIcon from 'assets/icons/met-logo.svg';
import AboutPanel from '../about-panel';
import { Button, MenuButton, type ButtonProps } from '../buttons';
import styles from './top-panel.module.scss';
import type { ViewerAction } from '../../types';
import type { PortableTextBlock } from '@portabletext/types';

export default function TopPanel(props: TopPanelProps) {
	const { showComments } = props;

	const optionsMenuOptionDescriptors: CheckBoxMenuOptionDescriptor[] = [
		{
			label: 'Show comments',
			value: 'showComments',
			checked: showComments,
			handleChange: (e) => {
				props.dispatch({
					type: e.target.checked ? 'show_comments' : 'hide_comments',
				});
			},
		},
	];

	// We render both of these and rely on CSS to display the correct one to prevent layout shifting on load.
	return (
		<>
			<TopPanelMobile
				optionsMenuOptionDescriptors={optionsMenuOptionDescriptors}
				{...props}
			/>
			<TopPanelDesktop
				optionsMenuOptionDescriptors={optionsMenuOptionDescriptors}
				{...props}
			/>
		</>
	);
}

function TopPanelMobile({
	title,
	loading,
	showAllSpreads,
	navigateToAboutPage,
	dispatch,
	optionsMenuOptionDescriptors,
}: TopPanelVariantProps) {
	return (
		<div className={[styles.container, styles.mobileContainer].join(' ')}>
			<h1 className={styles.titleWrapper}>
				<span className={styles.metLogoWrapper}>
					<MetLogoIcon className={styles.metLogo} />
				</span>
				<span className={styles.title}>
					<MarkdownParser>{title}</MarkdownParser>
				</span>
				{loading && (
					<span className={styles.spinnerWrapper} aria-hidden="true">
						<LoadingIcon className={styles.spinner} />
					</span>
				)}
			</h1>
			<div className={styles.buttonGroupLeft}>
				<Button
					label="Close"
					icon={<CloseIcon />}
					onClick={navigateToAboutPage}
					showLabel={false}
				/>
				<Button
					label="About"
					icon={<InfoIcon />}
					onClick={() => dispatch({ type: 'toggle_about_panel' })}
					showLabel={false}
				/>
				<OptionsMenuButton options={optionsMenuOptionDescriptors} />
			</div>
			<div className={styles.buttonGroupRight}>
				<Button
					label={showAllSpreads ? 'Back to spread' : 'All spreads'}
					icon={showAllSpreads ? <BackToSpreadIcon /> : <ViewAllSpreadsIcon />}
					showLabel={true}
					aria-haspopup={true}
					aria-expanded={showAllSpreads}
					onClick={() => dispatch({ type: 'toggle_all_spreads' })}
				/>
			</div>
		</div>
	);
}

function TopPanelDesktop({
	about,
	howToCite,
	title,
	loading,
	dispatch,
	optionsMenuOptionDescriptors,
	showAllSpreads,
	navigateToAboutPage,
	isMobile,
}: TopPanelVariantProps) {
	const handleOpenOrCloseAboutMenu = useCallback(
		(isOpen: boolean) => {
			// We need a guard here since we're always rendering both mobile and desktop versions.
			if (isMobile) return;
			dispatch({ type: isOpen ? 'show_about_panel' : 'hide_about_panel' });
		},
		[dispatch, isMobile],
	);

	return (
		<div className={[styles.container, styles.desktopContainer].join(' ')}>
			<div className={styles.buttonGroupLeft}>
				<Button
					label="Close"
					icon={<CloseIcon />}
					onClick={navigateToAboutPage}
					showLabel={true}
				/>
				<MenuButton
					label="About"
					icon={<InfoIcon />}
					menuContents={
						<div className={styles.tooltipContent}>
							<AboutPanel about={about} howToCite={howToCite} />
						</div>
					}
					showLabel={true}
					onOpenOrClose={handleOpenOrCloseAboutMenu}
				/>
				<OptionsMenuButton
					options={optionsMenuOptionDescriptors}
					showLabel={true}
				/>
			</div>
			<h1 className={styles.titleWrapper}>
				<span className={styles.title}>
					<MarkdownParser>{title}</MarkdownParser>
				</span>
				{loading && (
					<span className={styles.spinnerWrapper} aria-hidden="true">
						<LoadingIcon className={styles.spinner} />
					</span>
				)}
			</h1>
			<div className={styles.buttonGroupRight}>
				<Button
					label={showAllSpreads ? 'Back to spread' : 'View all spreads'}
					icon={showAllSpreads ? <BackToSpreadIcon /> : <ViewAllSpreadsIcon />}
					showLabel={true}
					aria-haspopup={true}
					aria-expanded={showAllSpreads}
					onClick={() => dispatch({ type: 'toggle_all_spreads' })}
				/>
			</div>
		</div>
	);
}

function OptionsMenuButton({ options, showLabel }: OptionsMenuButtonProps) {
	return (
		<MenuButton
			label="Options"
			showLabel={showLabel}
			icon={<CogIcon />}
			menuContents={
				<ul
					className={styles.optionsMenuOptions}
					role="listbox"
					aria-multiselectable="true"
				>
					{options.map((option) => (
						<li key={option.value}>
							<CheckBoxMenuOption
								option={option}
								onChange={option.handleChange}
							/>
						</li>
					))}
				</ul>
			}
		/>
	);
}

function CheckBoxMenuOption({ option, onChange }: CheckBoxMenuOptionProps) {
	const id = useId();

	return (
		<div className={styles.checkBoxMenuOption}>
			<input
				type="checkbox"
				checked={option.checked}
				aria-checked={option.checked}
				onChange={onChange}
				name="options"
				tabIndex={0}
				id={id}
				value={option.value}
			/>
			<label htmlFor={id}>{option.label}</label>
		</div>
	);
}

interface TopPanelProps {
	about: PortableTextBlock[];
	howToCite: string;
	title: string;
	isMobile: boolean;
	loading: boolean;
	showAllSpreads: boolean;
	showComments: boolean;
	navigateToAboutPage: () => void;
	dispatch: React.Dispatch<ViewerAction>;
}

interface TopPanelVariantProps extends TopPanelProps {
	optionsMenuOptionDescriptors: CheckBoxMenuOptionDescriptor[];
}

interface OptionsMenuButtonProps extends Omit<ButtonProps, 'icon'> {
	options: CheckBoxMenuOptionDescriptor[];
	showLabel?: boolean;
	icon?: ButtonProps['icon'];
}

interface CheckBoxMenuOptionProps {
	option: CheckBoxMenuOptionDescriptor;
	onChange: ChangeEventHandler<HTMLInputElement>;
}

interface CheckBoxMenuOptionDescriptor {
	label: string;
	value: string;
	checked: boolean;
	handleChange: ChangeEventHandler<HTMLInputElement>;
}
